(() => {
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const initMotion = (scope = document) => {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (reducedMotion || !gsap || !ScrollTrigger) return;

    if (!document.documentElement.classList.contains('bowbar-gsap-motion')) {
      gsap.registerPlugin(ScrollTrigger);
      document.documentElement.classList.add('bowbar-gsap-motion');
    }

    const animateGroup = (trigger, selectors, {
      start = 'top 88%',
      stagger = 0.09,
      y = 38,
      duration = 0.82,
      blur = 12,
      scale = 1
    } = {}) => {
      if (!trigger || trigger.dataset.bowbarMotionReady === 'true') return;
      const targets = selectors.flatMap((selector) => [...trigger.querySelectorAll(selector)]).filter(Boolean);
      if (!targets.length) {
        trigger.dataset.bowbarMotionReady = 'true';
        return;
      }

      trigger.dataset.bowbarMotionReady = 'true';
      gsap.set(targets, {
        opacity: 0,
        y,
        scale,
        filter: `blur(${blur}px)`,
        willChange: 'transform,opacity,filter'
      });

      ScrollTrigger.create({
        trigger,
        start,
        once: true,
        onEnter: () => gsap.to(targets, {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration,
          stagger,
          ease: 'power3.out',
          overwrite: true,
          onComplete: () => gsap.set(targets, { clearProps: 'willChange,transform,opacity,filter' })
        })
      });
    };

    scope.querySelectorAll?.('.bowbar-hero:not([data-bowbar-hero-motion-ready])').forEach((hero) => {
      hero.dataset.bowbarHeroMotionReady = 'true';
      const intro = [
        ...hero.querySelectorAll('.bowbar-hero__content .bowbar-kicker,.bowbar-hero__content h1,.bowbar-hero__lead,.bowbar-hero__actions')
      ];
      const shots = [...hero.querySelectorAll('.bowbar-hero__collage .bowbar-hero-shot')];
      const accents = [...hero.querySelectorAll('.bowbar-hero__mark,.bowbar-hero__bow')];

      gsap.set(intro, { opacity: 0, y: 30, filter: 'blur(10px)', willChange: 'transform,opacity,filter' });
      gsap.set(shots, { opacity: 0, y: 44, scale: 0.955, filter: 'blur(12px)', willChange: 'transform,opacity,filter' });
      gsap.set(accents, { opacity: 0, scale: 0.88, rotation: -6, willChange: 'transform,opacity' });

      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
      timeline.to(intro, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.78,
        stagger: 0.1,
        onComplete: () => gsap.set(intro, { clearProps: 'willChange,transform,opacity,filter' })
      }, 0.12);
      if (shots.length) timeline.to(shots, {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: 'blur(0px)',
        duration: 0.82,
        stagger: 0.14,
        onComplete: () => gsap.set(shots, { clearProps: 'willChange,transform,opacity,filter' })
      }, 0.34);
      if (accents.length) timeline.to(accents, {
        opacity: 1,
        scale: 1,
        rotation: 0,
        duration: 0.62,
        stagger: 0.1,
        onComplete: () => gsap.set(accents, { clearProps: 'willChange,transform,opacity' })
      }, 0.68);

      const mainImage = hero.querySelector('.bowbar-hero-shot--main img');
      const wrapper = mainImage?.parentElement;
      if (mainImage && wrapper) {
        gsap.fromTo(mainImage, { scale: 1.055, yPercent: -2.5 }, {
          scale: 1.055,
          yPercent: 3.5,
          ease: 'none',
          scrollTrigger: { trigger: wrapper, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
        });
      }
    });

    scope.querySelectorAll?.('.bowbar-shop:not([data-bowbar-shop-motion-ready])').forEach((section) => {
      section.dataset.bowbarShopMotionReady = 'true';
      const title = section.querySelector('.bowbar-section-title');
      const grid = section.querySelector('.bowbar-shop-grid');
      const categories = section.querySelector('.bowbar-category-links');

      if (title) {
        animateGroup(title, ['img', '.bowbar-stitch-label', 'h2'], {
          stagger: 0.11,
          start: 'top 77%',
          y: 26,
          duration: 0.78,
          blur: 9
        });
      }
      if (grid) {
        animateGroup(grid, ['.bowbar-shop-card'], {
          stagger: 0.13,
          start: 'top 71%',
          y: 38,
          duration: 0.9,
          blur: 11,
          scale: 0.985
        });
      }
      if (categories) {
        animateGroup(categories, ['a'], {
          stagger: 0.075,
          start: 'top 84%',
          y: 18,
          duration: 0.62,
          blur: 6
        });
      }
    });

    scope.querySelectorAll?.('.bowbar-new-arrivals:not([data-bowbar-new-arrivals-motion-ready])').forEach((section) => {
      section.dataset.bowbarNewArrivalsMotionReady = 'true';
      const head = section.querySelector('.bowbar-new-arrivals__head');
      const slider = section.querySelector('.bowbar-new-arrivals__splide');

      if (head) {
        animateGroup(head, ['.bowbar-stitch-label', 'h2', 'p', '.bowbar-text-link'], {
          stagger: 0.095,
          start: 'top 77%',
          y: 28,
          duration: 0.82,
          blur: 9
        });
      }

      if (slider) {
        const slides = [...slider.querySelectorAll('.splide__slide')];
        if (slides.length) {
          gsap.set(slides, {
            opacity: 0,
            y: 34,
            scale: 0.97,
            filter: 'blur(8px)',
            willChange: 'transform,opacity,filter'
          });
          ScrollTrigger.create({
            trigger: slider,
            start: 'top 69%',
            once: true,
            onEnter: () => {
              const center = window.innerWidth / 2;
              const ordered = slides
                .map((slide) => {
                  const rect = slide.getBoundingClientRect();
                  return { slide, distance: Math.abs(rect.left + rect.width / 2 - center) };
                })
                .sort((a, b) => a.distance - b.distance)
                .map((item) => item.slide);

              gsap.to(ordered, {
                opacity: 1,
                y: 0,
                scale: 1,
                filter: 'blur(0px)',
                duration: 0.82,
                stagger: 0.055,
                ease: 'power3.out',
                overwrite: true,
                onComplete: () => {
                  slider.classList.add('is-visible');
                  gsap.set(slides, { clearProps: 'willChange,transform,opacity,filter' });
                }
              });
            }
          });
        }
      }
    });

    scope.querySelectorAll?.('.bowbar-spotlight:not([data-bowbar-spotlight-motion-ready])').forEach((section) => {
      section.dataset.bowbarSpotlightMotionReady = 'true';
      const photo = section.querySelector('.bowbar-spotlight__photo');
      const copy = section.querySelector('.bowbar-spotlight__copy');
      const headingTargets = copy ? [
        copy.querySelector('.bowbar-stitch-label'),
        copy.querySelector('h2')
      ].filter(Boolean) : [];
      const detailTargets = copy ? [
        copy.querySelector('p'),
        ...copy.querySelectorAll('li'),
        copy.querySelector('.bowbar-button')
      ].filter(Boolean) : [];

      if (photo) {
        gsap.set(photo, { opacity: 0, y: 34, scale: 0.985, filter: 'blur(10px)', willChange: 'transform,opacity,filter' });
        ScrollTrigger.create({
          trigger: photo,
          start: 'top 76%',
          once: true,
          onEnter: () => gsap.to(photo, {
            opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.86, ease: 'power3.out',
            onComplete: () => gsap.set(photo, { clearProps: 'willChange,transform,opacity,filter' })
          })
        });
      }

      if (copy && headingTargets.length) {
        gsap.set(headingTargets, { opacity: 0, y: 30, filter: 'blur(10px)', willChange: 'transform,opacity,filter' });
        ScrollTrigger.create({
          trigger: copy,
          start: 'top 68%',
          once: true,
          onEnter: () => gsap.to(headingTargets, {
            opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.82, stagger: 0.11, ease: 'power3.out',
            onComplete: () => gsap.set(headingTargets, { clearProps: 'willChange,transform,opacity,filter' })
          })
        });
      }

      if (copy && detailTargets.length) {
        gsap.set(detailTargets, { opacity: 0, y: 24, filter: 'blur(8px)', willChange: 'transform,opacity,filter' });
        ScrollTrigger.create({
          trigger: copy,
          start: 'top 54%',
          once: true,
          onEnter: () => gsap.to(detailTargets, {
            opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.7, stagger: 0.075, ease: 'power3.out',
            onComplete: () => gsap.set(detailTargets, { clearProps: 'willChange,transform,opacity,filter' })
          })
        });
      }
    });

    scope.querySelectorAll?.('.bowbar-gallery:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, ['.bowbar-section-title', '.bowbar-gallery-card'], {
        stagger: 0.055,
        start: 'top 80%',
        y: 28,
        duration: 0.68,
        blur: 9,
        scale: 0.985
      });
    });

    scope.querySelectorAll?.('.bowbar-personalize:not([data-bowbar-personalize-motion-ready])').forEach((section) => {
      section.dataset.bowbarPersonalizeMotionReady = 'true';
      const media = section.querySelector('.bowbar-personalize__media');
      const content = section.querySelector('.bowbar-personalize__content');
      if (media) {
        gsap.set(media, { opacity: 0, y: 28, filter: 'blur(10px)', willChange: 'transform,opacity,filter' });
        ScrollTrigger.create({
          trigger: section,
          start: 'top 79%',
          once: true,
          onEnter: () => gsap.to(media, {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.9,
            ease: 'power3.out',
            onComplete: () => gsap.set(media, { clearProps: 'willChange,transform,opacity,filter' })
          })
        });
      }
      if (content) {
        animateGroup(content, [
          '.bowbar-personalize__heading .bowbar-stitch-label',
          '.bowbar-personalize__heading h2',
          '.bowbar-personalize__heading > p',
          '.bowbar-personalize__options > *',
          '.bowbar-personalize__cta'
        ], { stagger: 0.085, start: 'top 78%', y: 32, duration: 0.82 });
      }
    });

    scope.querySelectorAll?.('.bowbar-story:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, [
        '.bowbar-story__copy .bowbar-stitch-label',
        '.bowbar-story__copy h2',
        '.bowbar-story__text p',
        '.bowbar-story__copy .bowbar-text-link',
        '.bowbar-story__photos img'
      ], { stagger: 0.095, start: 'top 80%' });
    });

    scope.querySelectorAll?.('.bowbar-testimonials:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, ['.bowbar-section-title', '.bowbar-testimonial'], { stagger: 0.08, start: 'top 88%', y: 28, duration: 0.72 });
    });

    scope.querySelectorAll?.('.bowbar-final-cta:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, [':scope > img', '.bowbar-stitch-label', 'h2', '.bowbar-button'], { stagger: 0.11, start: 'top 82%', y: 32 });
    });

    scope.querySelectorAll?.('.bowbar-product-process:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, [':scope > div'], { stagger: 0.11, start: 'top 90%', y: 30 });
    });

    scope.querySelectorAll?.('.bowbar-product-inspiration:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, ['.bowbar-product-heading', '.bowbar-product-inspiration__card'], { stagger: 0.1, start: 'top 88%', y: 34 });
    });

    scope.querySelectorAll?.('.bowbar-product-details:not([data-bowbar-motion-ready])').forEach((section) => {
      animateGroup(section, ['.bowbar-product-heading', '.bowbar-product-details__grid > *'], { stagger: 0.085, start: 'top 88%', y: 30 });
    });

    scope.querySelectorAll?.('.bowbar-footer:not([data-bowbar-motion-ready])').forEach((footer) => {
      animateGroup(footer, ['.bowbar-footer__brand', '.bowbar-footer__links a', ':scope > p'], { stagger: 0.08, start: 'top 96%', y: 24, duration: 0.7, blur: 8 });
    });

    window.requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMotion());
  } else {
    initMotion();
  }

  document.addEventListener('shopify:section:load', (event) => initMotion(event.target));
})();